// Footer navigation data: the 4 footer columns (My Account | My Activity |
// Explore Elakay | Elakay & Roles), each a list of sub-groups. Data only --
// Footer.js / FooterNavGroups.jsx render it. Every link, route, icon and
// display condition is the one the footer already had; they are only
// regrouped. The business groups (Seller, Dropshipping In, External
// Dropshipping, Provider) and Role Management come from buildMyBusinessNav,
// never redefined here, so Switch Role, Seller/Provider separation and the
// Animation Settings gates stay those of myBusinessNav.js.
//
// Group: { key, title, kind: "links" | "auth" | "roleSwitch", items } -- every group is a
// collapsible menu that starts closed, whatever the role (FooterNavGroups.jsx).
// Item:  { key, route | action ("about" | "contact") + href fallback, icon, label, status, statusText }
import { FaUserCog, FaBoxes, FaShoppingCart, FaUserSecret, FaMoneyBill, FaHandsHelping, FaBriefcase } from "react-icons/fa";
import { MdToday, MdAccountBox, MdEmail } from "react-icons/md";
import { BsGridFill } from "react-icons/bs";
import { GrAscend } from "react-icons/gr";
import { elakayAutoText } from "../pageInside/dataElakay";
import { isAuthenticated } from "../auth";
import { Seller, Adminstrator, Family, canAccessMyShop, isSwitchRoleAvailable } from "../pageInside/Rolle";
import { buildMyBusinessNav } from "./myBusinessNav";

export const FOOTER_COLUMN_KEYS = ["account", "activity", "explore", "roles"];

const link = (key, route, icon, label) => ({ key, route, icon, label, status: "active", statusText: null });
// href: where the entry leads without JS / in a new tab (?aboutus opens About us on load).
const action = (key, actionName, href, label) => ({ key, action: actionName, href, label, status: "active", statusText: null });

const linksGroup = (key, title, items) => ({ key, title, kind: "links", items: items.filter(Boolean) });

// text: the footer's resolved language sections { menuList, title, buy, sell, helpContact }.
// options.emailMarketing: server-resolved canAccessEmailMarketing (see myBusinessNav sharedTools).
export const buildFooterNav = (infoSource, { text = {}, categories = [], emailMarketing } = {}) => {
  const { menuList = {}, title = {}, buy = {}, sell = {}, helpContact = {} } = text;
  const auth = Boolean(isAuthenticated());
  const isSeller = Boolean(Seller());
  const isFamily = auth && Boolean(Family());

  const business = auth ? buildMyBusinessNav(infoSource, { emailMarketing }) : [];
  const roleManagement = business.find((group) => group.key === "roleManagement");
  const businessGroups = business.filter((group) => group.key !== "roleManagement");

  // ── 1. My Account ────────────────────────────────────────────────────
  const account = [
    linksGroup("profile", elakayAutoText("h_ftr4_grp_profile", "Profile & Settings"), [
      link("profile", "/profile", FaUserSecret, menuList.myProfile),
      link("account", "/account", MdAccountBox, menuList.myAccount),
      auth && canAccessMyShop(infoSource) && link("userSetting", "/user-setting", FaUserCog, menuList.userSetting),
      // Email Inbox (spec §15): always visible, regardless of unreadEmailCount --
      // unlike the floating icon, this is the durable way to reach old emails.
      link("emailInbox", "/email-inbox", MdEmail, menuList.emailInbox || elakayAutoText("h_emk_inbox_menu_link", "Email Inbox")),
    ]),
    { key: "session", title: elakayAutoText("h_ftr4_grp_session", "Session"), kind: "auth", items: [] },
  ];

  // ── 2. My Activity ───────────────────────────────────────────────────
  const activity = [
    linksGroup("purchases", elakayAutoText("h_ftr_col_buyer", "Buyer"), [
      link("cart", "/cart", FaShoppingCart, menuList.myCart),
      link("orders", "/c/orders", FaBoxes, menuList.myOrders),
      link("purchased", "/purchased", FaMoneyBill, menuList.myPurchased),
    ]),
    linksGroup("applications", elakayAutoText("h_ftr4_grp_applications", "Job Applications"), [
      auth && link("myJobApplications", "/my-job-applications", FaBriefcase, menuList.myJobApplications || elakayAutoText("h_nav_my_job_applications", "Mes candidatures")),
    ]),
    ...businessGroups.map((group) => ({ ...group, kind: "links" })),
    linksGroup("sellerGuides", elakayAutoText("h_ftr4_grp_seller_guides", "Seller Guides"), isSeller ? [
      link("editProduct", "/how-to-Step?step=editProduct", null, sell.editProduct),
      link("linkCustomerShop", "/how-to-Step?step=LinkcustomerShop", null, sell.linkCostomertoShop),
    ] : []),
  ];

  // ── 3. Explore Elakay ────────────────────────────────────────────────
  const categoryItems = (categories || []).map((category, index) =>
    link(`category-${index}`, `/product/${category?.supCategoryID?._id}`, null, infoSource?.getCategoryNames?.(category)?.name?.[0])
  );
  const explore = [
    linksGroup("shop", elakayAutoText("h_ftr4_grp_shop", "Shop"), [
      link("todayDeal", "/deal-today", MdToday, menuList.todayDeal),
      link("allCategories", "/all-catageories", BsGridFill, menuList.allCategory),
    ]),
    linksGroup("categories", elakayAutoText("h_ftr4_grp_categories", "Categories"), categoryItems),
    linksGroup("opportunities", elakayAutoText("h_ftr4_grp_opportunities", "Opportunities"), [
      link("investments", "/investments", FaMoneyBill, menuList.investments || elakayAutoText("h_6de8d2a27f2c4c", "Investments")),
      link("jobs", "/jobs", FaBriefcase, menuList.jobs || elakayAutoText("h_elakay_jobs", "Jobs")),
    ]),
  ];

  // ── 4. Elakay & Roles: Elakay itself (about, support), then the roles ─
  const roles = [
    linksGroup("about", title.aboutElakay || elakayAutoText("h_ftr4_grp_about", "About Elakay"), [
      action("aboutUs", "about", "?aboutus=1", title.aboutUs),
      link("howWeDoIt", "/how-we-do-it", FaHandsHelping, menuList.howWeDoIt || elakayAutoText("h_hwdi_footer_link", "How We Do It in Elakay")),
    ]),
    // The footer listed /help-contact three times (two of them as "Help &
    // Contact"); it now appears once as Help & Contact plus the Terms entry.
    linksGroup("support", elakayAutoText("h_ftr4_grp_support", "Support"), [
      link("helpContact", "/help-contact", FaHandsHelping, menuList.helpContact || helpContact.HelpContact),
      link("terms", "/help-contact", null, helpContact.termsCondition),
      action("contactUs", "contact", "/help-contact", helpContact.ContactUs),
    ]),
    linksGroup("grow", elakayAutoText("h_ftr4_grp_grow", "Grow on Elakay"), [
      link("yourStep", "/your-step", GrAscend, menuList.yourStep),
      link("firstStep", "/how-to-Step?step=makeYourFstep", null, buy.makeYourFistStep),
      link("becomeRoyalty", "/how-to-Step?step=becomeRoyalty", null, buy.becomeRoyalty),
      link("becomeSeller", "/how-to-Step?step=becomeSeller", null, sell.becomeSeller),
      isSeller && link("becomeProvider", "/how-to-Step?step=becomeProvider", null, sell.becomeProvider),
    ]),
    // Visible ONLY to Associate..Family real accounts (isSwitchRoleAvailable):
    // nothing at all -- not even an empty title -- for Seller/Provider/Royalty/User.
    isSwitchRoleAvailable(infoSource) && {
      key: "roleSwitch", title: elakayAutoText("roleSwitch_menuLabel", "Switch Role"), kind: "roleSwitch", items: [],
    },
    roleManagement && { ...roleManagement, kind: "links" },
    linksGroup("administration", elakayAutoText("h_ftr4_grp_admin", "Elakay Administration"), [
      isFamily && link("investmentDashboard", "/elakay/investments-dashboard", FaMoneyBill, menuList.investmentDashboard || elakayAutoText("h_f7e94adac2f90b", "Investment Dashboard")),
      isFamily && link("jobDashboard", "/elakay/job-dashboard", FaBriefcase, menuList.jobDashboard || elakayAutoText("h_elakay_job_dashboard", "Job Dashboard")),
      isFamily && link("jobPositions", "/elakay/job-positions", FaBriefcase, menuList.jobPositionsAdmin || elakayAutoText("h_nav_job_positions_admin", "Liste des postes")),
      isFamily && link("jobApplications", "/elakay/job-applications", FaBriefcase, menuList.jobApplicationsAdmin || elakayAutoText("h_nav_job_applications_admin", "Toutes les candidatures")),
      auth && Adminstrator() && link("pageBuilder", "/pageBuilder", FaUserCog, menuList.pageBuilder || elakayAutoText("h_f29aa6896ad555", "Page Studio")),
    ]),
  ];

  const columnTitles = {
    account: elakayAutoText("h_ftr4_col_account", "My Account"),
    activity: elakayAutoText("h_mbiz_title", "My Business"),
    explore: elakayAutoText("h_ftr4_col_explore", "Explore Elakay"),
    roles: elakayAutoText("h_ftr4_col_roles", "Elakay & Roles"),
  };
  const columns = { account, activity, explore, roles };

  return FOOTER_COLUMN_KEYS.map((key) => ({
    key,
    title: columnTitles[key],
    groups: columns[key].filter((group) => group && (group.kind !== "links" || group.items.length)),
  }));
};
